import api from './apiClient';
import type { ApiResponse } from 'shared/types/api';

export type TemplateStatus = 'draft' | 'published';

export interface TemplateChecklistItem {
  _id?: string;
  name: string;
  description?: string;
  imageUrl?: string;
  isChecked: boolean;
}

export interface TemplateSection {
  _id?: string;
  name: string;
  description?: string;
  isMandatory: boolean;
  items: TemplateChecklistItem[];
}

export interface WorkPermitTemplate {
  _id: string;
  name: string;
  description?: string;
  category: string;
  status?: TemplateStatus;
  fields?: TemplateField[];
  requiredForms?: TemplateForm[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  // Sections (checklist) - matches backend model
  sections?: TemplateSection[];
  // Structured equipment items with optional images
  equipment?: Array<{ name: string; description?: string; imageUrl?: string; imageDownloadUrl?: string }>;
  // Resource arrays from backend
  chemicals?: string[];
  ppe?: string[];
  generalRisks?: string[];
  // Legacy campos adicionales del template
  workDescription?: string;
  defaultLocation?: string;
  defaultDuration?: number;
  identifiedRisks?: string[];
  toolsToUse?: string[];
  requiredPPE?: string[];
  equipmentType?: string[];
  safetyControls?: Array<{
    item: string;
    description?: string;
    checked: boolean;
    isCritical?: boolean;
    requiresRemedialPlan?: boolean;
    remedialPlan?: string;
    order?: number;
    category?: string;
    verificationMethod?: string;
    acceptanceCriteria?: string;
    _id?: string;
  }>;
  requiredApprovals?: string[];
  weatherConditions?: {
    maxWindSpeed?: number;
    prohibitedConditions?: string[];
  };
  code?: string;
  isGlobal?: boolean;
  usageCount?: number;
  version?: number;
}

export interface TemplateForm {
  form: string | any; // ID del formulario o objeto completo
  mandatory: boolean;
  order: number;
  condition?: {
    field: string;
    operator: string;
    value: any;
  };
}

export interface TemplateField {
  name: string;
  type: 'text' | 'number' | 'date' | 'boolean' | 'select' | 'multiselect';
  label: string;
  required: boolean;
  options?: string[];
  defaultValue?: any;
}

export interface WorkPermitTemplateFormData {
  name: string;
  description?: string;
  category: string;
  company?: string;
  isGlobal?: boolean;
  fields?: TemplateField[];
  isActive?: boolean;
  requiredForms?: TemplateForm[];
  equipment?: Array<{ name: string; description?: string; imageUrl?: string }>;
  chemicals?: string[];
  ppe?: string[];
  generalRisks?: string[];
  workDescription?: string;
  defaultLocation?: string;
  identifiedRisks?: string[];
  toolsToUse?: string[];
  requiredPPE?: string[];
  safetyControls?: Array<{
    item: string;
    description?: string;
    checked: boolean;
  }>;
  requiredApprovals?: string[];
  requiredDocuments?: Array<{
    name: string;
    required: boolean;
    description?: string;
  }>;
}

export interface TemplateCategory {
  _id: string;
  name: string;
  description?: string;
  templateCount: number;
}

class WorkPermitTemplateApiService {
  // Template CRUD operations
  async getAllTemplates(params?: {
    page?: number;
    limit?: number;
    category?: string;
    search?: string;
    status?: string;
    isActive?: boolean;
    companyId?: string;
    includeGlobals?: boolean;
  }): Promise<ApiResponse<WorkPermitTemplate[]>> {
    try {
      const result = await api.get('/work-permit-templates', params);

      return {
        success: true,
        data: result.data || [],
        pagination: result.pagination
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener templates'
      };
    }
  }

  async getTemplateById(templateId: string): Promise<ApiResponse<WorkPermitTemplate>> {
    try {
      const result = await api.get(`/work-permit-templates/${templateId}`);

      return {
        success: true,
        data: result.data
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener template'
      };
    }
  }

  async createTemplate(templateData: WorkPermitTemplateFormData): Promise<ApiResponse<WorkPermitTemplate>> {
    try {
      const result = await api.post('/work-permit-templates', templateData);

      return {
        success: true,
        data: result.data,
        message: 'Template creado exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al crear template'
      };
    }
  }

  async updateTemplate(templateId: string, templateData: Partial<WorkPermitTemplateFormData>): Promise<ApiResponse<WorkPermitTemplate>> {
    try {
      const result = await api.put(`/work-permit-templates/${templateId}`, templateData);

      return {
        success: true,
        data: result.data,
        message: 'Template actualizado exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al actualizar template'
      };
    }
  }

  async deleteTemplate(templateId: string): Promise<ApiResponse<void>> {
    try {
      await api.delete(`/work-permit-templates/${templateId}`);

      return {
        success: true,
        message: 'Template eliminado exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al eliminar template'
      };
    }
  }

  // Category operations
  async getCategories(): Promise<ApiResponse<TemplateCategory[]>> {
    try {
      const result = await api.get('/work-permit-templates/categories');

      return {
        success: true,
        data: result.data || []
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener categorías'
      };
    }
  }

  // Template activation/deactivation
  async toggleTemplateStatus(templateId: string): Promise<ApiResponse<WorkPermitTemplate>> {
    try {
      const result = await api.put(`/work-permit-templates/${templateId}/toggle-status`);

      return {
        success: true,
        data: result.data,
        message: 'Estado del template actualizado'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al cambiar estado del template'
      };
    }
  }

  // Clone template
  async cloneTemplate(templateId: string, newName: string): Promise<ApiResponse<WorkPermitTemplate>> {
    try {
      const result = await api.post(`/work-permit-templates/${templateId}/clone`, { name: newName });

      return {
        success: true,
        data: result.data,
        message: 'Template clonado exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al clonar template'
      };
    }
  }

  // Get templates by category
  async getTemplatesByCategory(category: string): Promise<ApiResponse<WorkPermitTemplate[]>> {
    try {
      const result = await api.get(`/work-permit-templates/category/${category}`);

      return {
        success: true,
        data: result.data || []
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener templates por categoría'
      };
    }
  }

  // Actualizar formularios de un template
  async updateTemplateForms(templateId: string, forms: TemplateForm[]): Promise<ApiResponse<WorkPermitTemplate>> {
    try {
      const result = await api.put(`/work-permit-templates/${templateId}/forms`, { forms });

      return {
        success: true,
        data: result.data,
        message: 'Formularios actualizados exitosamente'
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al actualizar formularios'
      };
    }
  }

  // Obtener formularios de un template
  async getTemplateForms(templateId: string): Promise<ApiResponse<TemplateForm[]>> {
    try {
      const result = await api.get(`/work-permit-templates/${templateId}/forms`);

      return {
        success: true,
        data: result.data || result.forms || []
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener formularios'
      };
    }
  }

  // Obtener formularios disponibles para una categoría
  async getAvailableFormsByCategory(category: string): Promise<ApiResponse<any[]>> {
    try {
      const result = await api.get(`/work-permit-templates/category/${category}/available-forms`);

      return {
        success: true,
        data: result.data || result.forms || []
      };
    } catch (error: unknown) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Error al obtener formularios disponibles'
      };
    }
  }
}

export const workPermitTemplateApi = new WorkPermitTemplateApiService();
